import { useContext, useState } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../../Context/GlobalContext";
import useClients from "../useClient";
const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
};

export default function useClientConfig() {
  const { fetchAll, clients, loading } = useClients();

  const { api, api2, anlookApi, adminId, latitude, longitude } =
    useContext(GlobalContext);

  const [busy, setBusy] = useState({});

  const setSpin = (id, action) => setBusy((p) => ({ ...p, [id]: action }));
  const clearSpin = (id) => setBusy((p) => ({ ...p, [id]: null }));

  const activate = async (userCode) => {
    setSpin(userCode, "activate");
    try {
      await api.post(`/Admin/client/activateClient`, {
        adminId,
        userid: userCode,
        latitude,
        longitude,
      });
      toast.success("Client activated");
      await fetchAll();
    } catch (e) {
      toast.error("Activation failed");
    } finally {
      clearSpin(userCode);
    }
  };

  const deactivate = async (userCode) => {
    setSpin(userCode, "deactivate");
    try {
      await api.post(`/Admin/client/deactivateClient`, {
        adminId,
        userid: userCode,
        latitude,
        longitude,
      });
      toast.success("Client deactivated");
      await fetchAll();
    } catch (e) {
      toast.error("Deactivation failed");
    } finally {
      clearSpin(userCode);
    }
  };

  const subscribe = async (userCode) => {
    setSpin(userCode, "subscribe");
    try {
      const r = await api.post(`/Account/v1/facebookSubscribe`, {
        userid: userCode,
      });
      r?.data?.responseCode === "00"
        ? toast.success("Webhook subscribed")
        : toast.error(r?.data?.responseMessage || "Subscription failed");
    } catch {
      toast.error("Unable to subscribe");
    } finally {
      clearSpin(userCode);
    }
  };

  const register = async (userCode) => {
    setSpin(userCode, "register");
    try {
      const r = await api.post(`/Account/v1/facebookRegisterMobileNumber`, {
        userid: userCode,
      });
      r?.data?.responseCode === "00"
        ? toast.success("Registration requested")
        : toast.error(r?.data?.responseMessage || "Registration failed");
    } catch {
      toast.error("Unable to register");
    } finally {
      clearSpin(userCode);
    }
  };
  const deRegister = async (userCode) => {
    setSpin(userCode, "deregister");

    try {
      const r = await api.post(`/Account/v1/facebookDeregisterMobileNumber`, {
        userid: userCode,
      });

      r?.data?.responseCode === "00"
        ? toast.success("Deregistered successfully")
        : toast.error(r?.data?.responseMessage || "Deregistration failed");
    } catch {
      toast.error("Unable to deregister");
    } finally {
      clearSpin(userCode);
    }
  };

  const fetchWhatsappCredentials = async (userid) => {
    try {
      const body = {
        userid,
        secret: "string",
        fromdate: "string",
        todate: "string",
        status: ["string"],
      };
      const r = await api.post(`/Whatsapp/getWhatsappCredentials`, body);
      if (r?.data?.responseCode === "00") return r.data.response;
      toast.error(r?.data?.responseMessage || "Failed to fetch credentials");
      return null;
    } catch {
      toast.error("Error fetching credentials");
      return null;
    }
  };

  const updateWhatsappCredentials = async ({
    userid,
    appId,
    businessAccId,
    phoneNo,
    token,
  }) => {
    setSpin(userid, "whatsapp");
    try {
      await api.post(`/Admin/client/configureWhatsappCredentials`, {
        adminId,
        latitude,
        longitude,
        userid,
        appId,
        businessAccId,
        token,
        phoneNoId: phoneNo,
      });
      toast.success("WhatsApp credentials updated");
    } catch {
      toast.error("Update failed");
    } finally {
      clearSpin(userid);
    }
  };

  const extendSubscription = async ({ userid, days }) => {
    setSpin(userid, "upgrade");
    try {
      const r = await api.post(`/Admin/v1.0/extendUserSubscription`, {
        userid,
        days,
      });
      r?.data?.responseCode === "00"
        ? toast.success(r?.data?.responseMessage || "Subscription extended")
        : toast.error(r?.data?.responseMessage || "Upgrade failed");
    } catch {
      toast.error("Error extending subscription");
    } finally {
      clearSpin(userid);
    }
  };

  const updatePaymentGateway = async ({ userid, paymentGateway }) => {
    setSpin(userid, "gateway");
    try {
      const r = await api.post(`/Admin/v1.0/updateClientPaymentGateway`, {
        adminId,
        userid,
        paymentGateway: paymentGateway?.trim(),
      });
      r?.data?.responseCode === "00"
        ? toast.success(r?.data?.responseMessage || "Gateway updated")
        : toast.error(r?.data?.responseMessage || "Gateway update failed");
    } catch {
      toast.error("Error updating gateway");
    } finally {
      clearSpin(userid);
    }
  };
  const fetchWhatsappMetaProfile = async (userid) => {
    try {
      const r = await api.post(`/Account/v1/getWhatsappMetaProfile`, {
        userid,
      });
      if (r?.data?.responseCode === "00") {
        return r?.data?.data || null;
      }
      toast.error(
        r?.data?.responseMessage || "Failed to load WhatsApp profile",
      );
      return null;
    } catch (e) {
      toast.error("Error loading WhatsApp profile");
      return null;
    }
  };

  const fetchClientOnboardingStatus = async (userid) => {
    try {
      const r = await api.post("/Admin/v1.0/getClientConfigurationStatus", {
        userid,
      });

      if (r?.data?.responseCode === "00" && Array.isArray(r.data.response)) {
        return r.data.response;
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch onboarding status",
      );
      return [];
    } catch (e) {
      toast.error("Error fetching onboarding status");
      return [];
    }
  };

  const registerBeneficiary = async ({ userid, form }) => {
    try {
      const r = await api2.post(
        "/api/Payout/AxisBank/BeneficiaryRegistration",
        {
          userid,
          ...form,
        },
      );

      if (r?.data?.responseCode === "00") {
        toast.success("Beneficiary added successfully");
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to add beneficiary");
      return false;
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage ||
          e?.response?.data?.message ||
          "API Error",
      );
      return false;
    }
  };

  // const saveClientConfiguration = async ({ adminId, userid, payload }) => {
  //   setSpin(userid, "config");
  //   try {
  //     const r = await api.post("/Admin/v1.0/insertRestaurantOrderDetails", {
  //       adminId,
  //       userid,
  //       ...payload,
  //     });

  //     if (r?.data?.responseCode === "00") {
  //       toast.success("Configuration updated successfully");
  //       return true;
  //     }

  //     toast.error(r?.data?.responseMessage || "Failed to update settings");
  //     return false;
  //   } catch (e) {
  //     toast.error("Failed to update settings");
  //     return false;
  //   } finally {
  //     clearSpin(userid);
  //   }
  // };

  const saveClientConfiguration = async ({ mobileNumber, userid, payload }) => {
    const cleanMobileNumber = String(mobileNumber || "")
      .replace(/\D/g, "")
      .trim();

    if (!cleanMobileNumber) {
      toast.error("Mobile number is required");
      return false;
    }

    if (cleanMobileNumber.length !== 10) {
      toast.error("Please enter valid 10 digit mobile number");
      return false;
    }

    setSpin(userid, "config");

    try {
      const requestPayload = {
        adminId: cleanMobileNumber,
        userid,
        ...payload,
      };

      console.log("insertRestaurantOrderDetails payload:", requestPayload);

      const r = await api.post(
        "/Admin/v1.0/insertRestaurantOrderDetails",
        requestPayload,
      );

      if (r?.data?.responseCode === "00") {
        toast.success("Configuration updated successfully");
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to update settings");

      return false;
    } catch (e) {
      console.error("insertRestaurantOrderDetails error:", e?.response || e);

      toast.error(
        e?.response?.data?.responseMessage || "Failed to update settings",
      );

      return false;
    } finally {
      clearSpin(userid);
    }
  };
  
  const fetchMerchantInfo = async (userid) => {
    try {
      const r = await api.post("/Website/getMerchantInfo", { userid });
      return r?.data?.details || null;
    } catch {
      toast.error("Failed to fetch merchant info");
      return null;
    }
  };

  const updateMerchantInfo = async (payload) => {
    try {
      await api.post("/Website/updateMerchantInfo", payload);
      toast.success("Merchant profile synchronized");
      return true;
    } catch {
      toast.error("Update failed");
      return false;
    }
  };
  /* ---------------- PAGES (CMS) ---------------- */

  const fetchPages = async (userid) => {
    try {
      const r = await api.post("/Website/getPages", { userid });
      return r?.data?.details || [];
    } catch {
      toast.error("Failed to fetch pages");
      return [];
    }
  };

  const fetchPageById = async ({ userid, page_id }) => {
    try {
      const r = await api.post("/Website/getPageById", {
        userid,
        id: page_id,
      });
      return r?.data?.details || null;
    } catch {
      toast.error("Failed to fetch page details");
      return null;
    }
  };

  const savePage = async ({
    userid,
    page_id,
    title,
    sequence,
    content,
    status,
  }) => {
    try {
      await api.post("/Website/updatePage", {
        userid,
        page_id,
        title,
        sequence,
        content,
        status,
      });

      toast.success(
        page_id ? "Page updated successfully" : "Page created successfully",
      );
      return true;
    } catch {
      toast.error("Failed to save page");
      return false;
    }
  };

  /* ---------------- CERTIFICATES ---------------- */

  const getCertificates = async (userid) => {
    try {
      const r = await api.post("/Website/getCertificates", { userid });
      return r?.data?.details || [];
    } catch {
      toast.error("Failed to fetch certificates");
      return [];
    }
  };

  const updateCertificate = async ({
    userid,
    certificate_id,
    certificate_name,
    certificate_image,
    status = "Y",
  }) => {
    try {
      const r = await api.post("/Website/updateCertificate", {
        userid,
        certificate_id,
        certificate_name,
        certificate_image,
        status,
      });

      if (r?.data?.responseCode === "00") {
        toast.success(r?.data?.responseMessage || "Certificate saved");
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to save certificate");
      return false;
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage || "Failed to save certificate",
      );
      return false;
    }
  };

  /* ---------------- BANNER ---------------- */

  const getBanner = async (userid) => {
    try {
      const r = await api.post("/Website/getBanner", { userid });
      const details = r?.data?.details;

      if (!details || (Array.isArray(details) && details.length === 0)) {
        return null;
      }

      return Array.isArray(details) ? details[0] : details;
    } catch {
      toast.error("Failed to fetch banner");
      return null;
    }
  };

  const updateBanner = async ({
    userid,
    banner_id,
    banner_url,
    background_url,
  }) => {
    try {
      await api.post("/Website/updateBanner", {
        userid,
        banner_id,
        banner_url,
        background_url,
      });

      toast.success(banner_id ? "Banner updated" : "Banner added");
      return true;
    } catch {
      toast.error("Failed to save banner");
      return false;
    }
  };

  const updateWhatsappProfile = async ({
    userid,
    messaging_product,
    about,
    address,
    description,
    vertical,
    email,
    websites,
    profile_picture_handle,
  }) => {
    try {
      const payload = {
        userid,
        messaging_product,
        about,
        address,
        description,
        vertical,
        email,
        websites,
        profile_picture_handle,
      };

      const r = await api.post("/Account/UpdateWhatsappProfile", payload);

      if (r?.data?.responseCode === "00") {
        toast.success("WhatsApp profile updated successfully");
        return true;
      }

      toast.error(
        r?.data?.responseMessage || "Failed to update WhatsApp profile",
      );
      return false;
    } catch (e) {
      console.error("UpdateWhatsappProfile error:", e);
      toast.error("Error updating WhatsApp profile");
      return false;
    }
  };

  const getWhatsappProfileHandle = async ({
    userid,
    filename,
    filetype,
    fileurl,
  }) => {
    try {
      const payload = {
        userid,
        filename,
        filetype,
        fileurl,
      };

      const r = await api.post("/Account/GetWhatsappProfileHandle", payload);

      if (r?.data?.responseCode === "00") {
        return r?.data?.data; // profile_picture_handle
      }

      toast.error(
        r?.data?.responseMessage || "Failed to get WhatsApp profile handle",
      );
      return null;
    } catch (e) {
      console.error("GetWhatsappProfileHandle error:", e);
      toast.error("Error getting WhatsApp profile handle");
      return null;
    }
  };

  const getWhatsappVerticals = async () => {
    try {
      const r = await api.post("/Account/GetVertical", {});

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch business categories",
      );
      return [];
    } catch (error) {
      console.error("GetVertical error:", error);
      toast.error("Error fetching business categories");
      return [];
    }
  };

  const updateCatalogueSizeId = async ({ clientId }) => {
    try {
      const r = await api.post("/Inventory/updateSizeIdItems", { clientId });

      if (r?.data?.responseCode === "00") {
        return r?.data || [];
      }

      toast.error(
        r?.data?.responseMessage || "Failed to update catalogue size id",
      );
      return [];
    } catch (error) {
      console.error("API error:", error);
      toast.error("Error updating size id");
      return [];
    }
  };

  const getWhatsappProfileDisplayName = async (userid) => {
    try {
      const r = await api.post("/Account/GetWhatsappProfileDisplayName", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage ||
          "Failed to fetch WhatsApp profile display name",
      );
      return [];
    } catch (error) {
      console.error("GetWhatsappProfileDisplayName error:", error);
      toast.error("Error fetching WhatsApp profile display name");
      return [];
    }
  };

  const updateWhatsappProfileDisplayName = async (name, userid) => {
    try {
      const payload = {
        name,
        userid,
      };

      const r = await api.post(
        "/Account/UpdateWhatsappProfileDisplayName",
        payload,
      );

      if (r?.data?.responseCode === "00") {
        toast.success("WhatsApp profile name updated successfully");
        return true;
      }

      toast.error(
        r?.data?.responseMessage || "Failed to update WhatsApp profile name",
      );
      return false;
    } catch (e) {
      console.error("UpdateWhatsappProfileDisplayName error:", e);
      toast.error("Error updating WhatsApp profile name");
      return false;
    }
  };

  const getWhatsappProfileDisplayNameStatus = async (userid) => {
    try {
      const r = await api.post("/Account/GetWhatsappProfileDisplayNameStatus", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage ||
          "Failed to fetch WhatsApp profile display name status",
      );
      return [];
    } catch (error) {
      console.error("GetWhatsappProfileDisplayName error:", error);
      toast.error("Error fetching WhatsApp profile display name");
      return [];
    }
  };

  const getWhatsappProfileSubscriptions = async (userid) => {
    try {
      const r = await api.post("/Account/GetWhatsappProfileSubscriptions", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage + " to fetch WhatsApp profile subscriptions" ||
          "Failed to fetch WhatsApp profile subscriptions",
      );
      return [];
    } catch (error) {
      console.error("GetWhatsappProfileSubscriptions error:", error);
      toast.error("Error fetching WhatsApp profile subscriptions");
      return [];
    }
  };

  const deleteWhatsappProfileSubscriptions = async (userid) => {
    try {
      const r = await api.post("/Account/DeleteWhatsappProfileSubscriptions", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage ||
          "Failed to fetch WhatsApp profile subscriptions",
      );
      return [];
    } catch (error) {
      console.error("GetWhatsappProfileSubscriptions error:", error);
      toast.error("Error fetching WhatsApp profile subscriptions");
      return [];
    }
  };

  const updateImageUrlOrganization = async ({ userid, imageUrl }) => {
    try {
      const payload = {
        userid,
        imageUrl,
      };

      const r = await api.post(
        "/Admin/v1.0/updateImageUrlOrganization",
        payload,
      );

      if (r?.data?.responseCode === "00") {
        return r?.data; // boolean true / false
      }

      toast.error(
        r?.data?.responseMessage || "Failed to update organization image url",
      );
      return null;
    } catch (e) {
      console.error("UpdateImageUrlOrganization error:", e);
      toast.error("Error updating organization image url");
      return null;
    }
  };

  const getBlockedMetaUsers = async (userId) => {
    try {
      const r = await api.post("/Account/GetBlockedMetaUsers", { userId });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch blocked meta users",
      );
      return [];
    } catch (error) {
      console.error("GetBlockedMetaUsers error:", error);
      toast.error("Error fetching blocked meta users");
      return [];
    }
  };

  const deleteBlockedMetaUsers = async (clientId, blockedUser) => {
    try {
      const payload = {
        messaging_product: "whatsapp",
        userid: clientId,
        block_users: [
          {
            user: blockedUser,
          },
        ],
      };

      const r = await api.post("/Account/DeleteBlockedMetaUsers", payload);

      if (r?.data?.responseCode === "00") {
        toast.success("User unblocked successfully");
        return r?.data || [];
      }

      toast.error(r?.data?.responseMessage || "Failed to unblock user");
      return [];
    } catch (error) {
      console.error("DeleteBlockedMetaUsers error:", error);
      toast.error("Error unblocking user");
      return [];
    }
  };

  const BlockMetaUser = async (clientId, blockedUser) => {
    try {
      const payload = {
        messaging_product: "whatsapp",
        userid: clientId,
        block_users: [
          {
            user: blockedUser,
          },
        ],
      };

      const r = await api.post("/Account/BlockMetaUser", payload);

      if (r?.data?.responseCode === "00") {
        {
          r.data.data ==
          "The last Message from the user was received 24 hrs ago"
            ? toast.error(
                "Cannot block user. Last message received over 24 hours ago.",
              )
            : toast.success(r.data.data || "User blocked successfully");
        }
        return r?.data || [];
      }

      toast.error(r?.data?.responseMessage || "Failed to block user");
      return [];
    } catch (error) {
      console.error("BlockMetaUser error:", error);
      toast.error("Error blocking user");
      return [];
    }
  };

  const getUserHealthCheck = async (clientId) => {
    try {
      const r = await api.post("/Account/GetUserHealthCheck", { clientId });

      if (r?.data?.responseCode === "00") {
        return r?.data || [];
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch user health check data",
      );
      return [];
    } catch (error) {
      console.error("GetUserHealthCheck error:", error);
      toast.error("Error fetching user health check data");
      return [];
    }
  };
  const updateGSTDetails = async ({ userid, gst, gstType, taxGst }) => {
    setSpin(userid, "gst");
    try {
      const r = await api.post("/Admin/updateGSTDetails", {
        fk_user_id: userid,
        gst: gst?.trim(),
        gst_type: gstType,
        tax_gst: taxGst?.trim(),
      });

      if (r?.data?.responseCode === "00") {
        toast.success(r?.data?.responseMessage || "GST details updated");
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to update GST details");
      return false;
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage || "Error updating GST details",
      );
      return false;
    } finally {
      clearSpin(userid);
    }
  };
  const getGSTDetails = async (userid) => {
    try {
      const r = await api.post("/Admin/getGSTDetails", {
        fk_user_id: userid,
      });

      if (r?.data?.responseCode === "00") {
        const row = Array.isArray(r?.data?.response)
          ? r.data.response[0]
          : null;
        return row || null;
      }

      toast.error(r?.data?.responseMessage || "Failed to fetch GST details");
      return null;
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage || "Error fetching GST details",
      );
      return null;
    }
  };

  const fetchRestaurantOrderDetails = async ({ adminId, userid }) => {
    try {
      const r = await api.post("/Admin/v1.0/getRestaurantOrderDetails", {
        adminId,
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return Array.isArray(r?.data?.response) ? r.data.response : [];
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch restaurant order details",
      );
      return [];
    } catch (e) {
      console.error("getRestaurantOrderDetails error:", e);
      toast.error("Error fetching restaurant order details");
      return [];
    }
  };
  const chaafoOnboarding = async ({ user_code, phone_no }) => {
    try {
      const cleanPhone = String(phone_no || "").replace(/\D/g, "");

      const phoneWithCountryCode = cleanPhone.startsWith("91")
        ? cleanPhone
        : `91${cleanPhone}`;

      const r = await api.post("/Whatsapp/chaafoOnboarding", {
        user_code,
        phone_no: phoneWithCountryCode,
      });

      if (r?.data?.responseCode === "00") {
        const row = Array.isArray(r?.data?.response)
          ? r.data.response[0]
          : null;

        if (!row?.phone_no_id) {
          toast.error("Phone No ID not found from onboarding API");
          return null;
        }

        return row;
      }

      toast.error(r?.data?.responseMessage || "Onboarding failed");
      return null;
    } catch (e) {
      console.error("chaafoOnboarding error:", e);
      toast.error("Error while onboarding client");
      return null;
    }
  };
  const encodeEmojisOnly = (text = "") => {
    return text
      .replace(/\r?\n/g, "\n")
      .replace(/[\p{Extended_Pictographic}]/gu, (emoji) => {
        return Array.from(emoji)
          .map((char) => {
            let encoded = "";

            for (let i = 0; i < char.length; i++) {
              encoded += `\\u${char.charCodeAt(i).toString(16).padStart(4, "0")}`;
            }

            return encoded;
          })
          .join("");
      });
  };

  const decodeEmojisOnly = (text = "") => {
    return text.replace(/\\u([\dA-Fa-f]{4})/g, (_, code) =>
      String.fromCharCode(parseInt(code, 16)),
    );
  };
  //
  // ---------------- CHATBOT CUSTOM MESSAGE ----------------
  const getClientCustomMessage = async (userid) => {
    try {
      const r = await api.post("/Admin/getClientCustomMessage", {
        us_code: userid,
      });

      if (r?.data?.responseCode === "00") {
        const response = r?.data?.response;
        const rawMsg =
          response?.message ||
          response?.custom_message ||
          (Array.isArray(response) ? response[0]?.message || response[0]?.custom_message : "") ||
          "";

        return decodeEmojisOnly(rawMsg || "");
      }

      return "";
    } catch (error) {
      toast.error("Failed to fetch chatbot message");
      return "";
    }
  };

  // ---------------- INSERT CHATBOT CUSTOM MESSAGE ----------------
  const insertClientCustomMessage = async ({ userid, customMessage }) => {
    setSpin(userid, "chatbotMessage");

    try {
      const r = await api.post("/Admin/InsertClientCustomMessage", {
        us_code: userid,
        custom_message: encodeEmojisOnly(customMessage),
      });

      if (r?.data?.responseCode === "00") {
        toast.success(r?.data?.responseMessage || "Chatbot message saved");
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to save chatbot message");
      return false;
    } catch (error) {
      toast.error("Error saving chatbot message");
      return false;
    } finally {
      clearSpin(userid);
    }
  };

  /* ---------------- GALLERY ---------------- */

  const getGallery = async (userid) => {
    try {
      const r = await api.post("/Website/getgallery", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        return r?.data?.data || [];
      }

      toast.error(r?.data?.responseMessage || "Failed to fetch gallery");
      return [];
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage || "Failed to fetch gallery",
      );
      return [];
    }
  };

  const updateGallery = async ({
    userid,
    image1 = "",
    image2 = "",
    image3 = "",
    image4 = "",
    image5 = "",
    image6 = "",
    image7 = "",
    image8 = "",
    image9 = "",
    image10 = "",
  }) => {
    try {
      const r = await api.post("/Website/updategallery", {
        userid,
        image1,
        image2,
        image3,
        image4,
        image5,
        image6,
        image7,
        image8,
        image9,
        image10,
      });

      if (r?.data?.responseCode === "00") {
        toast.success(
          r?.data?.responseMessage || "Gallery updated successfully",
        );
        return true;
      }

      toast.error(r?.data?.responseMessage || "Failed to update gallery");
      return false;
    } catch (e) {
      toast.error(
        e?.response?.data?.responseMessage || "Failed to update gallery",
      );
      return false;
    }
  };

  /* ---------------- WHY CHOOSE US ---------------- */

  const getChooseUs = async (userid) => {
    try {
      const r = await api.post("/Website/getchooseus", {
        userid,
      });

      if (r?.data?.responseCode === "00") {
        const chooseUsData = Array.isArray(r?.data?.data)
          ? r.data.data[0]
          : r?.data?.data;

        return chooseUsData || null;
      }

      toast.error(
        r?.data?.responseMessage || "Failed to fetch Why Choose Us details",
      );

      return null;
    } catch (error) {
      console.error("getChooseUs error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Failed to fetch Why Choose Us details",
      );

      return null;
    }
  };

  const updateChooseUs = async ({
    userid,
    section_title = "",
    section_image = "",
    section_description = "",
    title1 = "",
    desc1 = "",
    title2 = "",
    desc2 = "",
    title3 = "",
    desc3 = "",
    title4 = "",
    desc4 = "",
    title5 = "",
    desc5 = "",
    title6 = "",
    desc6 = "",
  }) => {
    setSpin(userid, "chooseUs");

    try {
      const payload = {
        userid,
        section_title,
        section_image,
        section_description,
        title1,
        desc1,
        title2,
        desc2,
        title3,
        desc3,
        title4,
        desc4,
        title5,
        desc5,
        title6,
        desc6,
      };

      const r = await api.post("/Website/updatechooseus", payload);

      if (r?.data?.responseCode === "00") {
        toast.success(
          r?.data?.responseMessage || "Why Choose Us updated successfully",
        );

        return true;
      }

      toast.error(
        r?.data?.responseMessage || "Failed to update Why Choose Us details",
      );

      return false;
    } catch (error) {
      console.error("updateChooseUs error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Failed to update Why Choose Us details",
      );

      return false;
    } finally {
      clearSpin(userid);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result);

      reader.onerror = () => reject(new Error("Unable to read selected image"));
    });
  };

  const getUploadedChooseUsImageUrl = (uploadResponse) => {
    if (!uploadResponse) {
      return "";
    }

    const responseData = uploadResponse?.response;
    const nestedData = uploadResponse?.data;

    return (
      uploadResponse?.image_url ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      nestedData?.image_url ||
      nestedData?.imageUrl ||
      nestedData?.url ||
      responseData?.image_url ||
      responseData?.imageUrl ||
      responseData?.url ||
      (typeof responseData === "string" ? responseData : "") ||
      ""
    );
  };

  const uploadChooseUsImage = async ({ userid, file }) => {
    if (!userid) {
      toast.error("Client ID is missing");
      return "";
    }

    if (!file) {
      toast.error("Please select an image");
      return "";
    }

    if (!file.type?.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return "";
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2 MB");
      return "";
    }

    try {
      const imageData = await fileToBase64(file);

      const payload = {
        userid,
        item_name: "choose_us_image",
        type: file.type || "IMG",
        image_data: imageData,
      };

      const r = await anlookApi.post("/v1/uploadItemImagev2.php",
        payload,
      );

      const uploadResponse = r?.data || {};

      const uploadedImageUrl = getUploadedChooseUsImageUrl(uploadResponse);

      const status = String(uploadResponse?.status ?? "");

      const responseCode = String(uploadResponse?.responseCode ?? "");

      const uploadSucceeded =
        status === "0" ||
        responseCode === "00" ||
        uploadResponse?.success === true ||
        Boolean(uploadedImageUrl);

      if (!uploadSucceeded) {
        toast.error(
          uploadResponse?.responseMessage ||
            uploadResponse?.message ||
            "Image upload failed",
        );

        return "";
      }

      if (!uploadedImageUrl) {
        console.error("Image URL missing in upload response:", uploadResponse);

        toast.error("Image uploaded but URL was not received");

        return "";
      }

      return uploadedImageUrl;
    } catch (error) {
      console.error("uploadChooseUsImage error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to upload Choose Us image",
      );

      return "";
    }
  };


const getFssaiNo = async (user_id) => {
  try {
    const r = await api.post("/Admin/getFssaiNo", {
      user_id,
    });

    if (r?.data?.responseCode === "00") {
      return r?.data?.response?.[0]?.fssai_no || "";
    }

    toast.error(
      r?.data?.responseMessage || "Failed to fetch FSSAI licence number"
    );

    return "";
  } catch (e) {
    console.error("getFssaiNo error:", e);

    toast.error(
      e?.response?.data?.responseMessage ||
        "Error fetching FSSAI licence number"
    );

    return "";
  }
};

const updateFssaiNo = async ({ user_id, fssai_no }) => {
  try {
    const r = await api.post("/Admin/updateFssaiNo", {
      user_id,
      fssai_no: fssai_no?.trim(),
    });

    if (r?.data?.responseCode === "00") {
      toast.success(
        r?.data?.responseMessage || "FSSAI licence number updated",
      );

      return true;
    }

    toast.error(
      r?.data?.responseMessage || "Failed to update FSSAI licence number",
    );

    return false;
  } catch (e) {
    console.error("updateFssaiNo error:", e);

    toast.error(
      e?.response?.data?.responseMessage ||
        "Error updating FSSAI licence number",
    );

    return false;
  }
};

  return {
    clients,
    loading,
    fetchAll,
    busy,
    fmt,
    // actions
    activate,
    deactivate,
    subscribe,
    register,
    deRegister,
    fetchWhatsappCredentials,
    updateWhatsappCredentials,
    extendSubscription,
    updatePaymentGateway,
    fetchWhatsappMetaProfile,
    fetchClientOnboardingStatus,
    registerBeneficiary,
    saveClientConfiguration,
    fetchMerchantInfo,
    updateMerchantInfo,
    fetchPages,
    fetchPageById,
    savePage,
    getCertificates,
    updateCertificate,
    getBanner,
    updateBanner,
    updateWhatsappProfile,
    getWhatsappProfileHandle,
    getWhatsappVerticals,
    updateCatalogueSizeId,
    getWhatsappProfileDisplayName,
    updateWhatsappProfileDisplayName,
    getWhatsappProfileDisplayNameStatus,
    getWhatsappProfileSubscriptions,
    deleteWhatsappProfileSubscriptions,
    updateImageUrlOrganization,
    getBlockedMetaUsers,
    deleteBlockedMetaUsers,
    BlockMetaUser,
    getUserHealthCheck,
    updateGSTDetails,
    getGSTDetails,
    fetchRestaurantOrderDetails,
    chaafoOnboarding,
    getClientCustomMessage,
    insertClientCustomMessage,
    getGallery,
    updateGallery,
    getChooseUs,
    updateChooseUs,
    uploadChooseUsImage,
      updateGSTDetails,
  getGSTDetails,

  getFssaiNo,
  updateFssaiNo,
  };
}